import { useReadContract } from 'wagmi';
import { type Address } from 'viem';
import { ERC20_ABI, STAKING_ABI } from '../constants';

/**
 * Enhanced contract stats interface
 */
export interface EnhancedContractStats {
  contractTotalStaked: bigint;
  contractTotalWeight: bigint;
  contractEmissionRate: bigint;
  contractDailyEmission: bigint;
  contractTotalClaimed: bigint;
  contractAvailableRewards: bigint;
  contractAvgMultiplier: bigint;
  contractEstimatedDaily: bigint;
}

/**
 * User dashboard interface
 */
export interface UserDashboard {
  userTotalStaked: bigint;
  userTotalWeight: bigint;
  userTotalPending: bigint;
  userPositionCount: bigint;
  userMultiplier: bigint;
  userShareOfPool: bigint;
}

/**
 * Lock configuration interface
 */
export interface LockConfig {
  minLockDays: bigint;
  maxLockDays: bigint;
  minMultiplier: bigint;
  maxMultiplier: bigint;
}

/**
 * Custom hook for all contract read operations
 * Uses enhanced contract functions for efficient batch reads
 * @param requiredChainId - Dynamic chainId from backend config (BSC or Sepolia)
 * @param farToken - Dynamic FAR token address from backend config
 * @param stakingContract - Dynamic staking contract address from backend config
 */
export const useContractReads = (
  address: Address | undefined,
  chainId: number | undefined,
  requiredChainId: number,
  farToken: Address,
  stakingContract: Address
) => {
  // Read FAR token balance
  const { data: farBalance, refetch: refetchBalance } = useReadContract({
    address: farToken,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 5000,
    },
  });

  // Read token allowance
  const { data: tokenAllowance, refetch: refetchAllowance } = useReadContract({
    address: farToken,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, stakingContract] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 3000,
    },
  });

  // Read user positions
  const { data: userPositions, refetch: refetchPositions } = useReadContract({
    address: stakingContract,
    abi: STAKING_ABI,
    functionName: 'getUserPositions',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 5000,
    },
  });

  // Read enhanced contract stats (replaces totalStaked, totalStakers, emissionRate)
  const { data: enhancedStats, refetch: refetchEnhancedStats } = useReadContract({
    address: stakingContract,
    abi: STAKING_ABI,
    functionName: 'getEnhancedContractStats',
    query: {
      enabled: chainId === requiredChainId,
      refetchInterval: 5000,
    },
  }) as { data: EnhancedContractStats | undefined; refetch: () => void };

  // Read user dashboard (user-specific aggregated data)
  const { data: userDashboard, refetch: refetchUserDashboard } = useReadContract({
    address: stakingContract,
    abi: STAKING_ABI,
    functionName: 'getUserDashboard',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 5000,
    },
  }) as { data: UserDashboard | undefined; refetch: () => void };

  // Read total pending rewards (for claim button visibility)
  const { data: totalPendingRewards, refetch: refetchTotalPendingRewards } = useReadContract({
    address: stakingContract,
    abi: STAKING_ABI,
    functionName: 'getTotalPendingRewards',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 3000,
    },
  });

  // Read active stakers count
  const { data: activeStakers } = useReadContract({
    address: stakingContract,
    abi: STAKING_ABI,
    functionName: 'getActiveStakersCount',
    query: {
      enabled: chainId === requiredChainId,
      refetchInterval: 10000,
    },
  });

  // Read user claimed rewards
  const { data: userClaimedRewards, refetch: refetchUserClaimedRewards } = useReadContract({
    address: stakingContract,
    abi: STAKING_ABI,
    functionName: 'getUserClaimedRewards',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 5000,
    },
  });

  // Read lock configuration from contract
  const { data: lockConfig } = useReadContract({
    address: stakingContract,
    abi: STAKING_ABI,
    functionName: 'getLockConfig',
    query: {
      enabled: chainId === requiredChainId,
    },
  }) as { data: LockConfig | undefined };

  return {
    // Token data
    farBalance,
    tokenAllowance,

    // Position data
    userPositions,

    // Enhanced contract stats (8 fields)
    enhancedStats,

    // User dashboard (6 fields)
    userDashboard,

    // Rewards
    totalPendingRewards,
    userClaimedRewards,

    // Other stats
    activeStakers,

    // Contract configuration
    lockConfig,

    // Refetch functions
    refetchBalance,
    refetchAllowance,
    refetchPositions,
    refetchEnhancedStats,
    refetchUserDashboard,
    refetchTotalPendingRewards,
    refetchUserClaimedRewards,
  };
};
